import { GitBranch } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import SourceReleasePanel from '../source-release/SourceReleasePanel'
import Panel from './Panel'
import {
  getApp,
  getProductionLogs,
  stopProductionBuild,
  type App,
} from '../../lib/api'
import { formatRelativeTime } from '../../lib/format'

interface GitPanelProps {
  appName: string
  appFolder?: string
  closeable?: boolean
  isMaximized?: boolean
  onClose?: () => void
  onMaximize?: () => void
  onOpenSession?: (terminalUrl: string, chromeUrl: string) => void
}

export default function GitPanel({
  appName,
  appFolder = '/home/coder/workspace/main',
  closeable = true,
  isMaximized,
  onClose,
  onMaximize,
  onOpenSession,
}: GitPanelProps) {
  const [app, setApp] = useState<App | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [liveLogs, setLiveLogs] = useState<{ build_logs: string; runtime_logs: string } | null>(null)
  const [liveLogsExpanded, setLiveLogsExpanded] = useState(false)
  const [liveLogsLoading, setLiveLogsLoading] = useState(false)

  const loadApp = useCallback(() => {
    let cancelled = false
    getApp(appName)
      .then((nextApp) => {
        if (!cancelled) setApp(nextApp)
      })
      .catch(() => {
        if (!cancelled) setApp(null)
      })
    return () => {
      cancelled = true
    }
  }, [appName])

  useEffect(() => {
    return loadApp()
  }, [loadApp])

  const handleStopLive = useCallback(async () => {
    if (!app) return
    setActionLoading('production-stop')
    try {
      await stopProductionBuild(app.name)
      // Refresh app state so production_status reflects the new value.
      loadApp()
    } catch (err) {
      // Errors surface via App load failure. SourceReleasePanel renders liveError via app.production_error_message.
      console.error('Failed to stop production:', err)
    } finally {
      setActionLoading(null)
    }
  }, [app, loadApp])

  const fetchLiveLogs = useCallback(async () => {
    setLiveLogsLoading(true)
    try {
      const result = await getProductionLogs(appName)
      setLiveLogs({ build_logs: result.build_logs, runtime_logs: result.runtime_logs })
    } catch (err) {
      console.error('Failed to fetch live logs:', err)
      setLiveLogs(null)
    } finally {
      setLiveLogsLoading(false)
    }
  }, [appName])

  const handleToggleLiveLogs = useCallback(() => {
    const next = !liveLogsExpanded
    setLiveLogsExpanded(next)
    if (next && !liveLogs) {
      void fetchLiveLogs()
    }
  }, [fetchLiveLogs, liveLogs, liveLogsExpanded])

  return (
    <Panel
      title="Source & Release"
      icon={<GitBranch className="w-4 h-4" />}
      closeable={closeable}
      isMaximized={isMaximized}
      onClose={onClose}
      onMaximize={onMaximize}
    >
      <SourceReleasePanel
        appName={appName}
        appFolder={appFolder}
        canEdit
        liveStatus={app?.production_status ?? null}
        liveError={app?.production_error_message ?? null}
        lastSuccessfulBuild={app?.last_successful_build ?? null}
        actionLoading={actionLoading}
        liveLogs={liveLogs}
        liveLogsExpanded={liveLogsExpanded}
        liveLogsLoading={liveLogsLoading}
        onOpenSession={onOpenSession ?? (() => undefined)}
        onStopLive={handleStopLive}
        onToggleLiveLogs={handleToggleLiveLogs}
        formatRelativeTime={formatRelativeTime}
        variant="workspace"
      />
    </Panel>
  )
}
